import React, {useEffect, useRef, useState, useCallback} from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDispatch } from 'react-redux';
import update from 'immutability-helper';
import { Modal, Button } from "react-bootstrap";
import { CustomerService, TransRoutesService } from '../../services';
import {
	PERSONAL_ROUTE_STATUS,
	isCustomerListActive,
	getCustomerAddressSlotOptions,
	buildRouteCustomerAddressFields,
	hasAddressSlot,
	matchAddressTextToSlot,
	formatStructuredAddress,
	resolveRouteCustomerAddress,
} from '../../shared';
import { GripVertical, Pencil, RecordCircleFill, XSquare } from 'react-bootstrap-icons';
import { transRoutesSlice } from '../../store';
import moment from 'moment';


const ItemTypes = {
  CARD: 'card',
  UNASSIGNED_CUSTOMER: 'UNASSIGNED_CUSTOMER',
};

/** Trip-only address override replaces displayed route assignment address. */
const displayRouteCustomerAddress = (cust, profile) => {
	return resolveRouteCustomerAddress(cust, profile) || '';
};

const ADDRESS_SLOT_LABELS = {
	addr1: 'Address 1',
	addr2: 'Address 2',
	addr3: 'Address 3',
	addr4: 'Address 4',
};

const removeCustomerIdsFromRouteList = (list = [], customerIdSet) => {
	const next = [];
	(list || []).forEach((item) => {
		if (Array.isArray(item?.customers)) {
			const remaining = item.customers.filter(
				(customer) => !customerIdSet.has(`${customer?.customer_id || ""}`)
			);
			if (remaining.length === 0) return;
			next.push({ ...item, customers: remaining });
			return;
		}
		if (customerIdSet.has(`${item?.customer_id || ""}`)) return;
		next.push(item);
	});
	return next;
};

const findCustomerOnRoute = (route, customerId) => {
	const id = `${customerId || ""}`;
	for (const item of route?.route_customer_list || []) {
		if (`${item?.customer_id || ""}` === id) return item;
		if (Array.isArray(item?.customers)) {
			const found = item.customers.find((customer) => `${customer?.customer_id || ""}` === id);
			if (found) return found;
		}
	}
	return null;
};

const Card = ({ content, index, moveCard }) => {
	const ref = useRef(null);
	const [{ handlerId, isOver, draggedIndex }, drop] = useDrop({
		accept: ItemTypes.CARD,
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
				isOver: monitor.isOver({ shallow: true }),
				draggedIndex: monitor.getItem()?.index,
			}
		},
		drop(item, monitor) {
			if (!ref.current) {
				return
			}
			const dragIndex = item.index
			const hoverIndex = index
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect()
			// Get vertical middle
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
			// Determine mouse position
			const clientOffset = monitor.getClientOffset()
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return
			}
			// Time to actually perform the action
			moveCard(dragIndex, hoverIndex)
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex
		},
	})
	const [{ isDragging }, drag] = useDrag({
		type: ItemTypes.CARD,
		item: () => {
			return { index }
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	})
	const opacity = isDragging ? 0 : 1
	const showDropTopIndicator = isOver && draggedIndex !== undefined && draggedIndex > index;
	const showDropBottomIndicator = isOver && draggedIndex !== undefined && draggedIndex < index;
	const dropZoneStyle = {
		opacity,
		paddingTop: '10px',
		paddingBottom: '10px',
		borderRadius: '8px',
		backgroundColor: isOver ? '#eef6ff' : 'transparent',
		borderTop: showDropTopIndicator ? '4px solid #0d6efd' : '4px solid transparent',
		borderBottom: showDropBottomIndicator ? '4px solid #0d6efd' : '4px solid transparent',
		transition: 'background-color 0.12s ease, border-color 0.12s ease'
	};
	drag(drop(ref))
	return (
		<div ref={ref} style={dropZoneStyle} data-handler-id={handlerId}>
			{content}
		</div>
	)
}

const RouteCustomerEditor = ({
	currentRoute,
	setNewCustomerList = (a) => {},
	viewMode,
	editFun = () => {},
	canEdit = true,
	onAddCustomer = null,
	scheduledAbsentCustomerIds = [],
	/** When set (e.g. Route Edit assignment tab), Save/Cancel render inside Customers Assigned, bottom-right */
	assignmentFooter = null,
	/** Set false when a parent wraps with DndProvider (e.g. RouteEdit assignment). */
	provideDndContext = true,
	/** Optional customer profiles for resolving/matching address slots on assigned customers. */
	customerProfiles = [],
	/**
	 * Optional same-day peer routes (e.g. daily template siblings). When provided, conflicts are
	 * resolved against these instead of fetching persisted routes.
	 */
	peerRoutes = null,
	/** Called after customers are removed from peerRoutes (daily template editing). */
	onPeerRoutesUpdated = null,
}) => {
	const dispatch = useDispatch();
	const {
		fetchAllRoutesSuccess,
		fetchAllTomorrowRoutesSuccess,
		fetchAllHisotryRoutesSuccess,
	} = transRoutesSlice.actions;
	const [customers, setCustomers] = useState([]);
	const [initializedRouteId, setInitializedRouteId] = useState(null);
	const [showAddPersonnelModal, setShowAddPersonnelModal] = useState(false);
	const [showAddAptGroupModal, setShowAddAptGroupModal] = useState(false);
	const [showEditAptGroupModal, setShowEditAptGroupModal] = useState(false);
	const [editGroupIndex, setEditGroupIndex] = useState(-1);
	const [customerOptions, setCustomerOptions] = useState([]);
	const [customerFilter, setCustomerFilter] = useState('');
	const [lastNameFilter, setLastNameFilter] = useState(undefined);
	const [newRouteCustomerList, setNewRouteCustomerList] = useState([]);
	const [newRouteGroupedCustomerList, setNewRouteGroupedCustomerList] = useState([]);
	const [newGroupName, setNewGroupName] = useState('');
	const [newGroupAddress, setNewGroupAddress] = useState('');
	const [transferringCustomers, setTransferringCustomers] = useState(false);

	const [currentItems, setCurrentItems] = useState(null);
	const scheduledAbsentIdsSet = new Set([
		...(Array.isArray(scheduledAbsentCustomerIds) ? scheduledAbsentCustomerIds : []),
		...((currentRoute?.route_customer_list || [])
			.filter((item) => item?.customer_route_status === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT)
			.map((item) => item?.customer_id)
			.filter(Boolean))
	]);
	const getAbsentNameStyle = (customerId) => (
		scheduledAbsentIdsSet.has(customerId) ? { color: '#dc3545', fontWeight: 700 } : {}
	);

	// Helper function to get all customer IDs already in the route
	const getAssignedCustomerIds = () => {
		const assignedIds = new Set();
		customers.forEach(item => {
			if (item.customer_id) {
				assignedIds.add(item.customer_id);
			}
			if (item.customers) {
				item.customers.forEach(c => {
					if (c.customer_id) {
						assignedIds.add(c.customer_id);
					}
				});
			}
		});
		return assignedIds;
	};

	const formatStructuredAddressLocal = (line1, line2, city, state, zipCode, note) => {
		return formatStructuredAddress(line1, line2, city, state, zipCode, note);
	};

	const getCustomerProfileById = (customerId) => {
		const id = `${customerId || ''}`;
		const fromProp = (customerProfiles || []).find((c) => `${c?.id}` === id);
		if (fromProp) return fromProp;
		return (customerOptions || []).find((c) => `${c?.id}` === id);
	};

	const getCustomerAddressOptions = (customer) => getCustomerAddressSlotOptions(customer);

	const getCustomerSearchAddresses = (customer) => {
		if (!customer) return [];
		const addresses = [
			formatStructuredAddressLocal(customer.address_line_1, customer.address_line_2, customer.city, customer.state, customer.zip_code),
			formatStructuredAddressLocal(customer.address2_line_1, customer.address2_line_2, customer.city2, customer.state2, customer.zip_code2),
			formatStructuredAddressLocal(customer.address3_line_1, customer.address3_line_2, customer.city3, customer.state3, customer.zip_code3),
			formatStructuredAddressLocal(customer.address4_line_1, customer.address4_line_2, customer.city4, customer.state4, customer.zip_code4),
		];
		return addresses.filter(address => (address || '').trim() !== '');
	};

	const getCustomerSearchText = (customer) => {
		const allAddressText = getCustomerSearchAddresses(customer).join(' ');
		return [
			customer?.name,
			customer?.id,
			customer?.apartment,
			allAddressText,
			// Keep legacy fields searchable for older records.
			customer?.address1,
			customer?.address2,
			customer?.address3,
			customer?.address4,
			customer?.address5,
		]
			.map(item => (item || '').toString().toLowerCase())
			.join(' ');
	};

	const matchesCustomerSearch = (customer, keyword) => {
		const normalizedKeyword = (keyword || '').toLowerCase().trim();
		if (!normalizedKeyword) return true;
		return getCustomerSearchText(customer).includes(normalizedKeyword);
	};

	useEffect(() => {
		const assignedIds = getAssignedCustomerIds();
		const availableCustomers = customerOptions?.filter((customer) => !assignedIds.has(customer.id));
		const filteredCustomers =
			availableCustomers
				?.filter(
					(customer) =>
						(lastNameFilter && customer.lastname?.toLowerCase().indexOf(lastNameFilter) === 0) || !lastNameFilter
				)
				?.filter((customer) => matchesCustomerSearch(customer, customerFilter)) || [];
		setCurrentItems(filteredCustomers);
	}, [customerOptions, customerFilter, lastNameFilter, customers]);

	const closeAddPersonnelModal = () => {
		setShowAddPersonnelModal(false);
		setNewRouteCustomerList([]);
	}

	const openAddPersonnelModal = () => {
		setLastNameFilter(undefined);
		if (customerOptions.length === 0) {
			CustomerService.getAllActiveCustomers().then((data) => {
				// Filter out discharged customers
				const filtered = (data.data || []).filter((customer) => isCustomerListActive(customer));
				setCustomerOptions(filtered);
			})
		}
		setShowAddPersonnelModal(true);
	}

	useEffect(() => {
		if (viewMode) return;
		if ((customerProfiles || []).length > 0) return;
		if (customerOptions.length > 0) return;
		CustomerService.getAllActiveCustomers()
			.then((data) => {
				const filtered = (data.data || []).filter((customer) => isCustomerListActive(customer));
				setCustomerOptions(filtered);
			})
			.catch(() => {});
	}, [viewMode, customerProfiles, customerOptions.length]);

	const closeAddAptGroupModal = () => {
		setShowAddAptGroupModal(false);
	}

	const openAddAptGroupModal = () => {
		setLastNameFilter(undefined);
		if (customerOptions.length === 0) {
			CustomerService.getAllActiveCustomers().then((data) => {
				// Filter out discharged customers
				const filtered = (data.data || []).filter((customer) => isCustomerListActive(customer));
				setCustomerOptions(filtered);
			})
		}
		setShowAddAptGroupModal(true);
	}

	const closeEditAptGroupModal = () => {
		setShowEditAptGroupModal(false);
		setNewGroupAddress('');
		setNewGroupName('');
		setNewRouteGroupedCustomerList([]);
		setEditGroupIndex(-1);
	}

	const openEditAptGroupModal = (index, group) => {
		setLastNameFilter(undefined);
		if (customerOptions.length === 0) {
			CustomerService.getAllActiveCustomers().then((data) => {
				// Filter out discharged customers
				const filtered = (data.data || []).filter((customer) => isCustomerListActive(customer));
				setCustomerOptions(filtered);
			})
		}
		setNewGroupAddress(group.customers[0].customer_group_address);
		setNewGroupName(group.customer_group);
		setNewRouteGroupedCustomerList(group.customers);
		setEditGroupIndex(index);
		setShowEditAptGroupModal(true);
	}

	const toggleItemToRouteList = (customer, value) => {
		if (value === 'false') {
			const slotOptions = getCustomerAddressOptions(customer);
			const first = slotOptions[0];
			const addressFields = first
				? buildRouteCustomerAddressFields(customer, first.slot)
				: { customer_address_slot: null, customer_address: '', customer_route_base_address: '' };
			setNewRouteCustomerList([].concat(newRouteCustomerList).concat([{
				customer_id: customer.id,
				customer_name: `${customer.name} ${customer.name_cn?.length > 0 ? `(${customer.name_cn})` : ``}`,
				...addressFields,
				customer_avatar: customer.avatar,
				customer_type: customer.type,
				customer_pickup_status: customer.pickup_status,
				customer_note: customer.notes_for_driver || '',
				customer_special_needs: customer.notes_for_driver || '',
				customer_phone: customer.phone || customer.mobile_phone || customer.home_phone,
				customer_route_status: PERSONAL_ROUTE_STATUS.NO_STATUS,
				customer_pickup_order: customers.length + newRouteCustomerList.length + 1,
				customer_table_id: customer.table_id,
				customer_language: customer.language
			}]));
		} else {
			setNewRouteCustomerList([].concat(newRouteCustomerList.filter((item) => item.customer_id !== customer.id)));
		}
	}

	const toggleGroupedItemToRouteList = (customer, value) => {
		if (value === 'false') {
			const slotOptions = getCustomerAddressOptions(customer);
			const first = slotOptions[0];
			const addressFields = first
				? buildRouteCustomerAddressFields(customer, first.slot)
				: { customer_address_slot: null, customer_address: '', customer_route_base_address: '' };
			setNewRouteGroupedCustomerList([].concat(newRouteGroupedCustomerList).concat([{
				customer_id: customer.id,
				customer_name: `${customer.name} ${customer.name_cn?.length > 0 ? `(${customer.name_cn})` : ``}`,
				...addressFields,
				customer_avatar: customer.avatar,
				customer_group: newGroupName,
				customer_group_address: newGroupAddress,
				customer_type: customer.type,
				customer_pickup_status: customer.pickup_status,
				customer_note: customer.notes_for_driver || '',
				customer_special_needs: customer.notes_for_driver || '',
				customer_phone: customer.phone || customer.mobile_phone || customer.home_phone,
				customer_route_status: PERSONAL_ROUTE_STATUS.NO_STATUS,
				customer_pickup_order: customers.length + 1,
				customer_table_id: customer.table_id,
				customer_language: customer.language
			}]));
		} else {
			setNewRouteGroupedCustomerList([].concat(newRouteGroupedCustomerList.filter((item) => item.customer_id !== customer.id)));
		}
	}


	const setCustomerAddressSlot = (id, slot, profile) => {
		const addressFields = buildRouteCustomerAddressFields(profile, slot);
		setNewRouteCustomerList(newRouteCustomerList.map((item) => {
			if (item.customer_id === id) {
				return {
					...item,
					...addressFields,
				}
			} else {
				return item;
			}
		}))
	}

	const setGroupedCustomerAddressSlot = (id, slot, profile) => {
		const addressFields = buildRouteCustomerAddressFields(profile, slot);
		setNewRouteGroupedCustomerList(newRouteGroupedCustomerList.map((item) => {
			if (item.customer_id === id) {
				return {
					...item,
					...addressFields,
				}
			} else {
				return item;
			}
		}))
	}

	const setAssignedCustomerAddressSlot = (customerId, slot, profile) => {
		const addressFields = buildRouteCustomerAddressFields(profile, slot);
		setCustomers((prev) => prev.map((item) => {
			if (item?.customers) {
				return {
					...item,
					customers: item.customers.map((customer) =>
						customer.customer_id === customerId
							? Object.assign({}, customer, addressFields)
							: customer
					),
				};
			}
			if (item?.customer_id === customerId) {
				return Object.assign({}, item, addressFields);
			}
			return item;
		}));
	};

	const removeGroupedCustomerFromSelection = (id) => {
		setNewRouteGroupedCustomerList((prevList) => prevList.filter((item) => item.customer_id !== id));
	}

	const setNewGroupNameAction = (value) => {
		setNewGroupName(value);
		for (const item of newRouteGroupedCustomerList) {
			item.customer_group = value;
		}
	}

	const setNewGroupAddressAction = (value) => {
		setNewGroupAddress(value);
		for (const item of newRouteGroupedCustomerList) {
			item.customer_group_address = value;
		}
	}

	const checkGroupRequiredField = () => {
		if ((!newGroupName || newGroupName.replace(' ', '') === '') || (!newGroupAddress || newGroupAddress.replace(' ', '') === '')) {
			window.alert('Group Name and Group Address is Required')
			return false;
		}
		return true;
	}

	const refreshRoutesStoreForScheduleDate = async (scheduleDate) => {
		const dateText = moment(
			scheduleDate,
			['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601],
			true
		).isValid()
			? moment(scheduleDate, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true).format('MM/DD/YYYY')
			: `${scheduleDate || ''}`;
		if (!dateText) return;
		const routes = (await TransRoutesService.getAll(dateText))?.data || [];
		const today = moment().startOf('day');
		const target = moment(dateText, 'MM/DD/YYYY', true).startOf('day');
		if (!target.isValid()) {
			dispatch(fetchAllRoutesSuccess(routes));
			return;
		}
		const diffDays = target.diff(today, 'days');
		if (diffDays === 0) {
			dispatch(fetchAllRoutesSuccess(routes));
		} else if (diffDays === 1) {
			dispatch(fetchAllTomorrowRoutesSuccess(routes));
		} else {
			dispatch(fetchAllHisotryRoutesSuccess(routes));
		}
	};

	const loadPeerRoutesForConflictCheck = async () => {
		if (Array.isArray(peerRoutes)) {
			return peerRoutes.filter((route) => {
				const routeId = `${route?.id || route?._id || ''}`;
				const currentId = `${currentRoute?.id || currentRoute?._id || ''}`;
				if (currentId && routeId && routeId === currentId) return false;
				if (currentRoute?.type && route?.type && route.type !== currentRoute.type) return false;
				return true;
			});
		}

		const scheduleDate = currentRoute?.schedule_date;
		if (!scheduleDate) return [];
		const dateText = moment(
			scheduleDate,
			['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601],
			true
		).isValid()
			? moment(scheduleDate, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true).format('MM/DD/YYYY')
			: `${scheduleDate}`;
		const routes = (await TransRoutesService.getAll(dateText))?.data || [];
		return routes.filter((route) => {
			const routeId = `${route?.id || ''}`;
			const currentId = `${currentRoute?.id || ''}`;
			if (currentId && routeId && routeId === currentId) return false;
			if (currentRoute?.type && route?.type && route.type !== currentRoute.type) return false;
			return true;
		});
	};

	/**
	 * If selected customers already exist on other same-day routes (same type), confirm and
	 * remove them from those routes before adding to the current route.
	 * Returns false if user cancels or transfer fails.
	 */
	const confirmAndTransferCustomersFromOtherRoutes = async (selectedCustomers = []) => {
		const selectedIds = selectedCustomers
			.map((customer) => `${customer?.customer_id || ''}`)
			.filter(Boolean);
		if (!selectedIds.length) return true;

		let peers = [];
		try {
			peers = await loadPeerRoutesForConflictCheck();
		} catch (error) {
			console.error('Failed to load peer routes for conflict check:', error);
			window.alert('Unable to check whether these customers exist on other routes. Please try again.');
			return false;
		}

		const conflicts = [];
		selectedIds.forEach((customerId) => {
			peers.forEach((route) => {
				const found = findCustomerOnRoute(route, customerId);
				if (!found) return;
				conflicts.push({
					customerId,
					customerName: found.customer_name || customerId,
					routeId: `${route?.id || route?._id || ''}`,
					routeName: route?.name || 'another route',
					route,
				});
			});
		});

		if (!conflicts.length) return true;

		const lines = conflicts.map(
			(item) => `- ${item.customerName} (currently on ${item.routeName})`
		);
		const confirmed = window.confirm(
			`The following customer(s) already exist on other route(s):\n\n${lines.join('\n')}\n\nConfirm to remove them from the original route(s) and add them to the current route?`
		);
		if (!confirmed) return false;

		const idsToRemove = new Set(conflicts.map((item) => item.customerId));
		const routesToUpdate = new Map();
		conflicts.forEach((item) => {
			if (!item.routeId) return;
			if (!routesToUpdate.has(item.routeId)) {
				routesToUpdate.set(item.routeId, item.route);
			}
		});

		try {
			if (Array.isArray(peerRoutes) && typeof onPeerRoutesUpdated === 'function') {
				const updatedPeers = (peerRoutes || []).map((route) => {
					const routeId = `${route?.id || route?._id || ''}`;
					if (!routesToUpdate.has(routeId)) return route;
					return {
						...route,
						route_customer_list: removeCustomerIdsFromRouteList(
							route?.route_customer_list || [],
							idsToRemove
						),
					};
				});
				onPeerRoutesUpdated(updatedPeers);
				return true;
			}

			const updates = [];
			routesToUpdate.forEach((route, routeId) => {
				const nextList = removeCustomerIdsFromRouteList(
					route?.route_customer_list || [],
					idsToRemove
				);
				updates.push(
					TransRoutesService.updateRoute(routeId, {
						...route,
						route_customer_list: nextList,
						updatedAt: new Date(),
						updatedBy: 'admin',
					})
				);
			});
			if (updates.length) {
				await Promise.all(updates);
				if (currentRoute?.schedule_date) {
					await refreshRoutesStoreForScheduleDate(currentRoute.schedule_date);
				}
			}
			return true;
		} catch (error) {
			console.error('Failed to transfer customers from other routes:', error);
			window.alert('Failed to remove customer(s) from the original route(s). Please try again.');
			return false;
		}
	};

	const addPersonnel = async () => {
		if (transferringCustomers) return;
		setTransferringCustomers(true);
		try {
			const canContinue = await confirmAndTransferCustomersFromOtherRoutes(newRouteCustomerList);
			if (!canContinue) return;

			const merged = [...customers];
			newRouteCustomerList.forEach((newCustomer) => {
				const existingIndex = merged.findIndex((item) => item?.customer_id === newCustomer?.customer_id);
				if (existingIndex >= 0) {
					// If the customer already exists, overwrite with latest selection (including address).
					merged[existingIndex] = {
						...merged[existingIndex],
						...newCustomer,
					};
				} else {
					merged.push(newCustomer);
				}
			});
			setCustomers(merged);
			setShowAddPersonnelModal(false);
			setNewRouteCustomerList([]);
		} finally {
			setTransferringCustomers(false);
		}
	}

	// Function to add a customer from external drop (like unassigned customers)
	const addCustomerFromDrop = useCallback((customerData, dropIndex = null) => {
		if (!customerData || !customerData.id) return;
		
		// Check if customer already exists
		const customerExists = customers.some(item => {
			if (item.customer_id) {
				return item.customer_id === customerData.id;
			}
			if (item.customers) {
				return item.customers.some(c => c.customer_id === customerData.id);
			}
			return false;
		});
		
		if (customerExists) return;
		
		const slotOptions = getCustomerAddressOptions(customerData);
		const first = slotOptions[0];
		const addressFields = first
			? buildRouteCustomerAddressFields(customerData, first.slot)
			: { customer_address_slot: null, customer_address: '', customer_route_base_address: '' };
		const newCustomer = {
			customer_id: customerData.id,
			customer_name: `${customerData.name} ${customerData.name_cn?.length > 0 ? `(${customerData.name_cn})` : ``}`,
			...addressFields,
			customer_avatar: customerData.avatar,
			customer_type: customerData.type,
			customer_pickup_status: customerData.pickup_status,
			customer_note: customerData.notes_for_driver || '',
			customer_special_needs: customerData.notes_for_driver || '',
			customer_phone: customerData.phone || customerData.mobile_phone || customerData.home_phone,
			customer_route_status: PERSONAL_ROUTE_STATUS.NO_STATUS,
			customer_pickup_order: customers.length + 1,
			customer_table_id: customerData.table_id,
			customer_language: customerData.language
		};
		
		setCustomers(prevCustomers => {
			if (dropIndex !== null && dropIndex >= 0 && dropIndex <= prevCustomers.length) {
				// Insert at specific position
				const newList = [...prevCustomers];
				newList.splice(dropIndex, 0, newCustomer);
				return newList;
			} else {
				// Add to end
				return [...prevCustomers, newCustomer];
			}
		});
	}, [customers]);

	const addAptGroup = async () => {
		if (transferringCustomers) return;
		if (!checkGroupRequiredField()) return;
		setTransferringCustomers(true);
		try {
			const canContinue = await confirmAndTransferCustomersFromOtherRoutes(newRouteGroupedCustomerList);
			if (!canContinue) return;

			const result = [].concat(customers).concat([{
				customers: newRouteGroupedCustomerList,
				customer_pickup_order: customers.length + 1,
				customer_group: newGroupName,
			}]);
			setCustomers(result.filter((item, pos) => result.indexOf(item) === pos));
			setShowAddAptGroupModal(false);
			setNewRouteGroupedCustomerList([]);
			setNewGroupAddress('');
			setNewGroupName('');
			setEditGroupIndex(-1);
		} finally {
			setTransferringCustomers(false);
		}
	}

	const editAptGroup = async () => {
		if (transferringCustomers) return;
		if (!checkGroupRequiredField()) return;
		setTransferringCustomers(true);
		try {
			const canContinue = await confirmAndTransferCustomersFromOtherRoutes(newRouteGroupedCustomerList);
			if (!canContinue) return;

			const result = [].concat(customers);
			result[editGroupIndex] = {
				...result[editGroupIndex],
				customers: newRouteGroupedCustomerList,
				customer_group: newGroupName,
			}
			setCustomers(result.filter((item, pos) => result.indexOf(item) === pos));
			setShowEditAptGroupModal(false);
			setNewGroupAddress('');
			setNewGroupName('');
			setNewRouteGroupedCustomerList([]);
			setEditGroupIndex(-1);
		} finally {
			setTransferringCustomers(false);
		}
	}

	// Only initialize customers from currentRoute when the route ID changes
	// Don't reset when currentRoute object reference changes (which happens on every render)
	// Note: template sub-document routes use _id instead of id
	const currentRouteId = currentRoute?.id || currentRoute?._id;
	useEffect(() => {
		if (currentRouteId && currentRouteId !== initializedRouteId) {
			setCustomers(getRouteCustomersWithGroups());
			setInitializedRouteId(currentRouteId);
		}
	}, [currentRouteId, initializedRouteId])
	const getRouteCustomersWithGroups = () => {
		const customerList = currentRoute?.route_customer_list?.map((item) => {
			const clone = Object.assign({}, item, {
				routeType: currentRoute.type,
				routeId: currentRoute.id || currentRoute._id,
			});
			const addr = (clone.customer_address || '').toString().trim();
			const hasOverride = (clone.customer_address_override || '').toString().trim();
			const hasBase = (clone.customer_route_base_address || '').toString().trim();
			if (!hasBase && !hasOverride && addr) {
				clone.customer_route_base_address = clone.customer_address;
			}
			return clone;
		});
		const result = {};
		if (customerList) {
			for (const customer of customerList) {
				if (customer.customer_group) {
					if (result[customer.customer_group]) {
						result[customer.customer_group].push(customer);
					} else {
						result[customer.customer_group] = [];
						result[customer.customer_group].push(customer);
					}
				} else {
					if (result.no_group) {
						result.no_group.push(customer);
					} else {
						result.no_group = [];
						result.no_group.push(customer);
					}
				}
			}
		}
		let finalResult = [];
		for (const key of Object.keys(result)) {
			if (key === 'no_group') {
				finalResult = finalResult.concat(result[key]);
			} else {
				finalResult.push({
					customer_pickup_order: result[key][0].customer_pickup_order,
					customer_group: key,
					customers: result[key]
				})
			}
		}
		return finalResult.sort((a, b) => a.customer_pickup_order - b.customer_pickup_order);
	}

	const deleteCustomer = (id) => {
		if (!window.confirm('Are you sure you want to remove this customer from the route?')) {
			return;
		}
		setCustomers(customers.filter((customer) => customer.customer_id !== id));
	}

	const deleteGroup = (index) => {
		if (!window.confirm('Are you sure you want to remove this group from the route?')) {
			return;
		}
		const arr = [].concat(customers);
		arr.splice(index, 1);
		setCustomers(arr);
	}

	const reorderItems = useCallback((dragIndex, hoverIndex) => {
		setCustomers((prevCards) => {
			return update(prevCards, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, prevCards[dragIndex]]
				]
			})
		});
	
	}, []);

	const renderAddressSlotRadios = (routeCustomer, profile, onSelectSlot) => {
		const slotOptions = getCustomerAddressOptions(profile);
		if (!slotOptions.length) {
			return <small className="text-muted">No profile addresses available.</small>;
		}
		const isLegacy = !hasAddressSlot(routeCustomer);
		const legacyAddress =
			(routeCustomer?.customer_address_override || '').toString().trim() ||
			(routeCustomer?.customer_address || '').toString().trim();
		const suggestedSlot = isLegacy ? matchAddressTextToSlot(profile, legacyAddress) : null;
		const selectedSlot = hasAddressSlot(routeCustomer)
			? routeCustomer.customer_address_slot
			: suggestedSlot;

		return (
			<div className="mt-1">
				{isLegacy && (
					<div className="text-success mb-1" style={{ fontSize: '12px', whiteSpace: 'normal' }}>
						This customer still uses a legacy saved address. Please select Address 1–4 below
						{suggestedSlot
							? ` (suggested: ${ADDRESS_SLOT_LABELS[suggestedSlot] || suggestedSlot}).`
							: '.'}
					</div>
				)}
				{slotOptions.map((option) => (
					<div key={`${routeCustomer.customer_id}-${option.slot}`}>
						<input
							className="me-2"
							name={`${routeCustomer.customer_id}-address-slot`}
							type="radio"
							value={option.slot}
							checked={selectedSlot === option.slot}
							onChange={() => onSelectSlot(option.slot)}
						/>
						<small>
							{ADDRESS_SLOT_LABELS[option.slot] || option.slot}: {option.address}
							{isLegacy && suggestedSlot === option.slot ? ' (suggested)' : ''}
						</small>
					</div>
				))}
			</div>
		);
	};

	const Items = ({ currentItems }) => {
		return currentItems?.map(
			(customer) => {
				const addressOptions = getCustomerAddressOptions(customer);
				const selected = newRouteCustomerList.find((item) => item.customer_id === customer.id);
				const customerDisplayName = customer?.name || '';
				const customerChineseName = customer?.name_cn || '';
				return <div key={customer.id} className="option-item">
				<input className="me-4 mt-2" type="checkbox" checked={selected!==undefined} value={selected!==undefined} onChange={(e) => toggleItemToRouteList(customer, e.target.value)}/>
				<div>
					<div>{`${customerDisplayName}${customerChineseName ? `(${customerChineseName})` : ''}`}</div>
					{selected && (<div>
						{addressOptions.map((option) => (
							<div key={`${customer.id}-address-${option.slot}`}>
								<input
									className="me-4"
									name={`${customer.id}-address`}
									type="radio"
									onChange={() => setCustomerAddressSlot(customer.id, option.slot, customer)}
									value={option.slot}
									checked={selected?.customer_address_slot === option.slot}
								/>
								<small>{ADDRESS_SLOT_LABELS[option.slot] || option.slot}: {option.address}</small>
							</div>
						))}
					</div>)}
				</div>
			</div>
			}
		)
	};

	const ItemsGroup = ({ currentItems }) => {
		const assignedIds = getAssignedCustomerIds();
		return currentItems?.filter(customer => !assignedIds.has(customer.id)).filter((customer) => matchesCustomerSearch(customer, customerFilter)).map(
			(customer) => {
				const addressOptions = getCustomerAddressOptions(customer);
				const selected = newRouteGroupedCustomerList.find((item) => item.customer_id === customer.id);
				const customerDisplayName = customer?.name || '';
				const customerChineseName = customer?.name_cn || '';
				return <div key={customer.id} className="option-item">
				<input className="me-4 mt-2" type="checkbox" checked={selected!==undefined} value={selected!==undefined} onChange={(e) => toggleGroupedItemToRouteList(customer, e.target.value)}/>
				<div>
					<div>{`${customerDisplayName}${customerChineseName ? `(${customerChineseName})` : ''}`}</div>
					{selected && (<div>
						{addressOptions.map((option) => (
							<div key={`${customer.id}-group-address-${option.slot}`}>
								<input
									className="me-4"
									name={`${customer.id}-address`}
									type="radio"
									onChange={() => setGroupedCustomerAddressSlot(customer.id, option.slot, customer)}
									value={option.slot}
									checked={selected?.customer_address_slot === option.slot}
								/>
								<small>{ADDRESS_SLOT_LABELS[option.slot] || option.slot}: {option.address}</small>
							</div>
						))}
					</div>)}
				</div>
			</div>
			}
		)
	}

	const getCurrentAssignedNumber = () => {
		let count = 0;
		for (const item of customers) {
			if (item.customers) {
				for (const customer of item.customers) {
					count++;
				}
			} else {
				count++;
			}
		}
		return count;
	}

	// Expose addCustomerFromDrop via callback
	useEffect(() => {
		if (onAddCustomer && typeof onAddCustomer === 'function') {
			onAddCustomer(addCustomerFromDrop);
		}
	}, [addCustomerFromDrop, onAddCustomer]);

	useEffect(() => {
		const result = [];
		for (const item of customers) {
			if (item.customer_group) {
				for (const customer of item.customers) {
					customer.customer_pickup_order = customers.indexOf(item);
					result.push(customer);
				}
			} else {
				item.customer_pickup_order = customers.indexOf(item);
				result.push(item);
			}
		}
		setNewCustomerList(result);
	}, [customers])

	// Create a drop zone for unassigned customers
	const CustomersDropZone = ({ children }) => {
		const [{ isOver }, drop] = useDrop({
			accept: ItemTypes.UNASSIGNED_CUSTOMER,
			drop: (item, monitor) => {
				// Child end-zone already handled the drop.
				if (monitor.didDrop()) return;
				if (addCustomerFromDrop && item && item.id) {
					addCustomerFromDrop(item, null); // Add to end
				}
				return { dropped: true };
			},
			collect: (monitor) => ({
				isOver: monitor.isOver({ shallow: false }),
			}),
		});

		return (
			<div 
				ref={drop} 
				className="customers-container mb-4 customers-container--assigned-drop"
				style={{ 
					backgroundColor: isOver ? '#f0f0f0' : 'transparent',
					border: isOver ? '2px dashed #0066B1' : '2px dashed transparent',
					borderRadius: '4px',
					padding: isOver ? '8px' : '0'
				}}
			>
				{children}
			</div>
		);
	};

	/** Drop below the last stop (empty “next stop” row + remaining list space) → new stop at end. */
	const EndStopDropZone = ({ children }) => {
		const [{ isOver, canDrop }, drop] = useDrop({
			accept: [ItemTypes.CARD, ItemTypes.UNASSIGNED_CUSTOMER],
			drop: (item, monitor) => {
				if (monitor.didDrop()) return;
				const itemType = monitor.getItemType();
				if (itemType === ItemTypes.CARD) {
					const dragIndex = item?.index;
					if (typeof dragIndex !== 'number' || dragIndex < 0) return;
					if (dragIndex === customers.length - 1) return;
					reorderItems(dragIndex, customers.length);
					item.index = customers.length - 1;
					return { dropped: true };
				}
				if (itemType === ItemTypes.UNASSIGNED_CUSTOMER && item?.id) {
					addCustomerFromDrop(item, null);
					return { dropped: true };
				}
			},
			collect: (monitor) => ({
				isOver: monitor.isOver({ shallow: true }),
				canDrop: monitor.canDrop(),
			}),
		});

		const active = isOver && canDrop;
		return (
			<div
				ref={drop}
				className={`customers-assigned-end-drop${active ? ' customers-assigned-end-drop--active' : ''}`}
			>
				{children}
				<div className="customers-assigned-end-drop__filler" aria-hidden="true" />
			</div>
		);
	};

	const editorContent = (
			<>
			<div className={!viewMode ? 'route-customer-editor-assignment' : undefined}>
			{ !viewMode && <h6 className="text-primary">Customers Assigned ({getCurrentAssignedNumber()})</h6>}
			{ viewMode && <h6 className="text-primary">Route Assignment {canEdit && <button className="btn btn-sm btn-primary" onClick={() => editFun('assignment')}><Pencil size={16} className="me-2"></Pencil>Edit </button>}</h6>}
			{!viewMode && <CustomersDropZone>
				{customers.map((item, index) => {
					if (item?.customers) {
						return <Card key={index} index={index} moveCard={reorderItems} content={(<div className="customers-dnd-item-container">
							<div className="stop-index"><span>{`Stop ${index+1}`}</span><RecordCircleFill size={16} color={"#0066B1"} className="ms-2"></RecordCircleFill> </div>
							<GripVertical className="me-4" size={20}></GripVertical>
							<div className="customer-dnd-item" onClick={() => openEditAptGroupModal(index, item)}>
								<span className="me-2">{item.customer_group} </span> <span>{item.customers[0]?.customer_group_address}</span>
								<div className="customer-dnd-item-content">{item.customers.map(customer => {
									const profile = getCustomerProfileById(customer.customer_id);
									return (
									<div key={customer.customer_id}>
									
									<small className="me-2" style={getAbsentNameStyle(customer.customer_id)}>{customer.customer_name}</small>
									<small className="me-2">{displayRouteCustomerAddress(customer, profile)}</small>
									<small className="me-2">{customer.customer_pickup_status}</small>
									{!hasAddressSlot(customer) && profile && (
										<div onClick={(e) => e.stopPropagation()}>
											{renderAddressSlotRadios(customer, profile, (slot) =>
												setAssignedCustomerAddressSlot(customer.customer_id, slot, profile)
											)}
										</div>
									)}
									</div>);
								})}
								</div>
							</div>
							<div className="customer-delete-btn"><button className="btn btn-default" onClick={()=> deleteGroup(index)}><XSquare size={14}></XSquare></button></div>
						</div>)}></Card>
					} else {
						const profile = getCustomerProfileById(item.customer_id);
						return <Card key={index} index={index} moveCard={reorderItems} content={<div className="customers-dnd-item-container">
									<div className="stop-index"><span>{`Stop ${index+1}`}</span><RecordCircleFill size={16} color={"#0066B1"} className="ms-2"></RecordCircleFill> </div>
								<GripVertical className="me-4" size={20}></GripVertical>
								<div className="customer-dnd-item">
									<span style={getAbsentNameStyle(item.customer_id)}>{item.customer_name} </span>
										<small className="me-2">{displayRouteCustomerAddress(item, profile)}</small>
										<small className="me-2">{item.customer_pickup_status}</small>
										{!hasAddressSlot(item) && profile && (
											renderAddressSlotRadios(item, profile, (slot) =>
												setAssignedCustomerAddressSlot(item.customer_id, slot, profile)
											)
										)}
								</div>
								<div className="customer-delete-btn"><button onClick={() => deleteCustomer(item.customer_id)} className="btn btn-default"><XSquare size={14}></XSquare> </button></div>
							</div>}>

						</Card>
					}
				})}
				<EndStopDropZone>
					<div className="new-customers-dnd-item-container">
						<div className="stop-index"><span>{`Stop ${customers?.length+1}`}</span><RecordCircleFill size={16} color={"#ccc"} className="ms-2"></RecordCircleFill> </div>
						<div>
							<button className="btn btn-primary btn-sm me-2 mb-2" onClick={() => openAddPersonnelModal()}> + Add Personnel </button>
							<button className="btn btn-primary btn-sm me-2 mb-2" onClick={() => openAddAptGroupModal()}> + Add Apt Group </button>
						</div>
					</div>
				</EndStopDropZone>
			</CustomersDropZone>}
			{assignmentFooter && !viewMode && (
				<div className="route-customer-editor-assignment-footer">
					<div className="d-flex justify-content-end align-items-center flex-wrap" style={{ gap: '8px' }}>
						<button type="button" className="btn btn-default btn-sm" onClick={assignmentFooter.onCancel}>
							Cancel
						</button>
						<button type="button" className="btn btn-primary btn-sm" onClick={assignmentFooter.onSave}>
							Save
						</button>
					</div>
					{assignmentFooter.errorMessage && (
						<div className="alert alert-danger mt-3 mb-0" role="alert">
							{assignmentFooter.errorMessage}
						</div>
					)}
				</div>
			)}
			{
				viewMode && <div className="customers-container mb-4">
					{customers.map((item, index) => {
						if (item?.customers) {
							return <div className="customers-dnd-item-container" key={index}>
								<div className="stop-index"><span>{`Stop ${index+1}`}</span><RecordCircleFill size={16} color={"#0066B1"} className="ms-2"></RecordCircleFill> </div>
								<div className="customer-dnd-item" onClick={() => canEdit && openEditAptGroupModal(index, item)} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
									<span className="me-2">{item.customer_group} </span> <span>{item.customers[0]?.customer_group_address}</span>
									<div className="customer-dnd-item-content">{item.customers.map(customer => 
										<div key={customer.customer_id}>
										
										<small className="me-2" style={getAbsentNameStyle(customer.customer_id)}>{customer.customer_name}</small>
										<small className="me-2">{displayRouteCustomerAddress(customer, getCustomerProfileById(customer.customer_id))}</small>
										<small className="me-2">{customer.customer_pickup_status}</small>
										</div>)}
									</div>
								</div>
							</div>
						} else {
							return <div className="customers-dnd-item-container" key={index}>
								<div className="stop-index"><span>{`Stop ${index+1}`}</span><RecordCircleFill size={16} color={"#0066B1"} className="ms-2"></RecordCircleFill> </div>
								<div className="customer-dnd-item">
									<span style={getAbsentNameStyle(item.customer_id)}>{item.customer_name} </span>
										<small className="me-2">{displayRouteCustomerAddress(item, getCustomerProfileById(item.customer_id))}</small>
										<small className="me-2">{item.customer_pickup_status}</small>
								</div>
							</div>
						}
					})}
				</div>
			}
			</div>
			<Modal show={showAddPersonnelModal} onHide={() => closeAddPersonnelModal()}>
        <Modal.Header closeButton>
          <Modal.Title>Add Personnel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
						<div className="app-main-content-fields-section">
							<div className="me-4">
								<div className="field-label">Type in UserId OR Name OR Address to Search
								</div>
								<input type="text" className="mb-4" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}/>
							</div>
							
						</div>
						<div>
							<div className="app-main-content-fields-section">
								{['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map(item => {
									return	<a key={item} className="me-2" onClick={() => {setLastNameFilter(item?.toLowerCase())} }>{item}</a>
								})}
							</div>
						</div>
						<a className="mb-4" onClick={() => setLastNameFilter(undefined)}>Clear All</a>
						<div className="customers-container mt-4">
							<div style={{ maxHeight: '420px', overflowY: 'auto' }}>
								<Items currentItems={currentItems} />
							</div>
						</div>
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={() => closeAddPersonnelModal()}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => addPersonnel()} disabled={transferringCustomers}>
						{transferringCustomers ? 'Adding…' : 'Add Personnel'}
          </Button>
        </Modal.Footer>
      </Modal>

			<Modal show={showAddAptGroupModal} onHide={() => closeAddAptGroupModal()}>
        <Modal.Header closeButton>
          <Modal.Title>Add Apt Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
						<div className="app-main-content-fields-section">
							<div className="me-4">
								<div className="field-label">Group Name
									<span className="required">*</span>
								</div>
								<input type="text" value={newGroupName} onChange={(e) => setNewGroupNameAction(e.target.value)}/>
							</div>
							<div className="me-4">
								<div className="field-label">Group Address
									<span className="required">*</span>
								</div>
								<input type="text" value={newGroupAddress} onChange={(e) => setNewGroupAddressAction(e.target.value)}/>
							</div>
						</div>
						<div className="app-main-content-fields-section">
							<div className="me-4">
								<div className="field-label">Type in user Id or Name to Search
								</div>
								<input type="text" className="mb-4" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}/>
							</div>
							
						</div>
						<div>
							<div className="mb-4">
								{['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map(item => {
									return	<a key={item} className="me-2" onClick={() => {setLastNameFilter(item?.toLowerCase())} }>{item}</a>
								})}
								
							</div>
						</div>
						<a className="mb-4" onClick={() => setLastNameFilter(undefined)}>Clear All</a>
						<div className="customers-container mt-4">
							<div style={{ maxHeight: '420px', overflowY: 'auto' }}>
								<ItemsGroup currentItems={currentItems} />
							</div>
						</div>
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" size="sm" onClick={() => closeAddAptGroupModal()}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => addAptGroup()} disabled={transferringCustomers}>
						{transferringCustomers ? 'Adding…' : 'Add Apt Group'}
          </Button>
        </Modal.Footer>
      </Modal>

			<Modal show={showEditAptGroupModal} onHide={() => closeEditAptGroupModal()}>
        <Modal.Header closeButton>
          <Modal.Title>Update Apt Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
						<div className="app-main-content-fields-section">
							<div className="me-4">
								<div className="field-label">Group Name
									<span className="required">*</span>
								</div>
								<input type="text" value={newGroupName} onChange={(e) => setNewGroupNameAction(e.target.value)}/>
							</div>
							<div className="me-4">
								<div className="field-label">Group Address
									<span className="required">*</span>
								</div>
								<input type="text" value={newGroupAddress} onChange={(e) => setNewGroupAddressAction(e.target.value)}/>
							</div>
						</div>
						<div className="app-main-content-fields-section">
							<div className="me-4" style={{ width: '100%' }}>
								<div className="field-label">Selected Customers ({newRouteGroupedCustomerList.length})</div>
								{newRouteGroupedCustomerList.length === 0 ? (
									<small className="text-muted">No customer selected yet.</small>
								) : (
									<div className="customers-container" style={{ maxHeight: '180px', overflowY: 'auto', padding: '8px' }}>
										{newRouteGroupedCustomerList.map((customer) => (
											<div
												key={`selected-group-customer-${customer.customer_id}`}
												className="d-flex align-items-center justify-content-between mb-2"
												style={{ gap: '12px' }}
											>
												<div>
													<div><small>{customer.customer_name}</small></div>
													<div><small className="text-muted">{displayRouteCustomerAddress(customer, getCustomerProfileById(customer.customer_id))}</small></div>
												</div>
												<Button
													variant="outline-danger"
													size="sm"
													onClick={() => removeGroupedCustomerFromSelection(customer.customer_id)}
												>
													Delete
												</Button>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
						<div className="app-main-content-fields-section">
							<div className="me-4">
								<div className="field-label">Type in user Id or Name to Search
								</div>
								<input type="text" className="mb-4" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}/>
							</div>
							
						</div>
						<div>
							<div className="mb-4">
								{['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map(item => {
									return	<a key={item} className="me-2" onClick={() => {setLastNameFilter(item?.toLowerCase())} }>{item}</a>
								})}
								
							</div>
						</div>
						<a className="mb-4" onClick={() => setLastNameFilter(undefined)}>Clear All</a>
						<div className="customers-container mt-4">
							<div style={{ maxHeight: '420px', overflowY: 'auto' }}>
								<ItemsGroup currentItems={currentItems} />
							</div>
						</div>
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" size="sm" onClick={() => closeEditAptGroupModal()}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => editAptGroup()} disabled={transferringCustomers}>
						{transferringCustomers ? 'Updating…' : 'Update Apt Group'}
          </Button>
        </Modal.Footer>
      </Modal>
			</>
	);

	if (provideDndContext) {
		return <DndProvider backend={HTML5Backend}>{editorContent}</DndProvider>;
	}
	return editorContent;
};

export default RouteCustomerEditor;